import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageContainer from '../components/layout/PageContainer';
import LoadingSpinner from '../components/common/LoadingSpinner';

interface Article {
  id: string;
  title: string;
  image: string;
  category: string;
  date: string;
  author: string;
  content: string;
}

interface NewsItem {
  id: string | number;
  // ... add other properties as needed
}

const NewsDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await fetch(`https://us-central1-appdizquetem.cloudfunctions.net/getNewsDetails?id=${id}`);
        const data = await response.json();
        setArticle(data);
      } catch (error) {
        console.error('Error loading article:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (!article) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center justify-center h-64">
          <h1 className="text-2xl font-semibold text-white mb-4">
            Artigo não encontrado
          </h1>
          <Link
            to="/news"
            className="text-primary hover:text-primary/80 transition-colors"
          >
            Voltar para notícias
          </Link>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Link
          to="/news"
          className="inline-flex items-center text-white/60 hover:text-white mb-6 transition-colors"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Voltar para notícias
        </Link>

        <article className="bg-white/10 backdrop-blur-lg rounded-xl overflow-hidden">
          <div className="w-full h-64 md:h-96">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-primary text-sm font-medium">
                {article.category}
              </span>
              <span className="text-white/40 text-sm">
                {new Date(article.date).toLocaleDateString('pt-BR')}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-semibold text-white mb-4">
              {article.title}
            </h1>
            <div className="text-white/40 text-sm mb-6">
              Por {article.author}
            </div>
            <div className="prose prose-invert max-w-none">
              {article.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-white/80 mb-4">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </article>
      </motion.div>
    </PageContainer>
  );
};

export default NewsDetail; 